<script lang="ts" setup>
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import OmniTable from "@/components/omni/OmniTable.vue";
import SearchField from "@/components/inputs/SearchField.vue";
import PageHeader from "@/components/header/PageHeader.vue";
import TableFilterRow from "@/components/tables/TableFilterRow.vue";
import TableFilter from "@/components/tables/TableFilter.vue";
import DatePicker from "@/components/pickers/datePicker/DatePicker.vue";
import DispatcherSelect from "@/components/selects/DispatcherSelect.vue";
import { useAppStore } from "@/store/app.store";
import { useSnackbarStore } from "@/store/snackbar.store";
import { useDateTime } from "@/composables/useDateTime";
import { useBaseView } from "@/composables/useBaseView";
import { API } from "@/api/api";
import type { LogRecordDto } from "@/api/service/dto/log-record.dto";
import type { LogsFilter } from "@/types/logs";

defineOptions({ name: "LogsPage" });

const { t } = useI18n();
const { dateTZ, toStartDate, toEndDate } = useDateTime();
const { setTitle } = useBaseView();

const appStore = useAppStore();
const snackbarStore = useSnackbarStore();
const api = new API();

const defaultFilter = (): LogsFilter => ({
  dispatcherId: null,
  timeFrom: null,
  timeTo: null,
  action: "",
});

const headers = [
  {
    title: t("dispatchers.log-action"),
    key: "action",
    sortable: false,
  },
  {
    title: t("dispatchers.dispatcher"),
    key: "dispatcher",
    sortable: false,
  },
  {
    title: t("general.description"),
    key: "description",
    sortable: false,
  },
];

const filter = ref<LogsFilter>(defaultFilter());
const items = ref<LogRecordDto[]>([]);
const loading = ref(true);

const refresh = async () => {
  const tz = appStore.dispatcher?.timezone;
  const params = {
    dispatcherId: filter.value.dispatcherId ?? null,
    timeFrom: toStartDate(filter.value.timeFrom, tz) || null,
    timeTo: toEndDate(filter.value.timeTo, tz) || null,
    action: filter.value.action || null,
    pageSize: 100,
  };

  loading.value = true;

  const resp = await api.service.fetchLogs(params);
  if (resp.success && resp.data) {
    items.value = resp.data;
  } else {
    snackbarStore.showSnackbarError(resp.error?.message || "Fetching logs failed");
  }

  loading.value = false;
};

const clear = () => {
  filter.value = defaultFilter();
  refresh();
};

const formatCreatedDate = (created: string) => {
  if (!appStore.dispatcher?.timezone) {
    return "";
  }

  return dateTZ(created, appStore.dispatcher.timezone);
};

onMounted(async () => {
  setTitle(t("dispatchers.logs"));
  await refresh();
});
</script>

<template>
  <v-container :fluid="true" class="pt-6 pb-10 pl-6 pr-6 height-100">
    <page-header header="Logs"></page-header>
    <table-filter :expandable="false" :filters="filter" @apply="refresh" @clear="clear">
      <template #header>
        <table-filter-row :divider="false" :label="t('general.filter-main')" icon="mdi-filter-variant">
          <v-col cols="12" sm="2">
            <dispatcher-select v-model="filter.dispatcherId" @update:model-value="refresh" />
          </v-col>
          <v-col cols="12" sm="2">
            <date-picker v-model="filter.timeFrom" :label="t('general.date-from')" @search="refresh" />
          </v-col>
          <v-col cols="12" sm="2">
            <date-picker v-model="filter.timeTo" :label="t('general.date-to')" @search="refresh" />
          </v-col>
          <v-col cols="12" sm="2">
            <search-field v-model="filter.action" :label="t('dispatchers.log-action')" @search="refresh" />
          </v-col>
        </table-filter-row>
      </template>
    </table-filter>

    <omni-table
      :headers="headers"
      :items="items"
      :loading="loading"
      :mobile-breakpoint="0"
      :server-data="false"
      hide-default-footer
    >
      <template #[`item.action`]="{ item }">
        <div>{{ item.action }}</div>
        <div v-if="item.entityId" class="text-caption text-grey-darken-1">
          {{ item.entityId }}
        </div>
      </template>
      <template #[`item.dispatcher`]="{ item }">
        <div>{{ item.dispatcher.name }}</div>
        <div class="text-caption text-grey-darken-1">
          {{ formatCreatedDate(item.created) }}
        </div>
      </template>
      <template #[`item.description`]="{ item }">
        <div class="description-cell">{{ item.description }}</div>
      </template>
    </omni-table>
  </v-container>
</template>

<style lang="scss" scoped>
.description-cell {
  white-space: normal;
  word-break: break-word;
  padding: 8px 0;
}
</style>
